import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { shouldBlockRemindersForPet } from "@/lib/utils/pet-death";
import type { Database } from "@/integrations/supabase/types";
import { addDays, formatISO, subDays } from "date-fns";
import { toISODateOnlyLocal, toLocalNoonISOString } from "@/lib/date-utils";
import type { AppointmentInput } from "./useAppointments";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import {
  filterToLatestVaccinations,
  getVaccinationNextItemId,
  type VaccinationKeyInput,
} from "@/lib/applications/pending-utils";
import i18n from "@/i18n";

type PetApplicationRow = Database["public"]["Tables"]["pet_applications"]["Row"];
type PetApplicationInsert = Database["public"]["Tables"]["pet_applications"]["Insert"];
type PetApplicationUpdate = Database["public"]["Tables"]["pet_applications"]["Update"];
type ApplicationItemRelation = {
  id?: string;
  name?: string | null;
  description?: string | null;
  sale_price?: number | null;
  product_subtype?: { name?: string | null } | null;
} | null;
type PetApplication = Omit<PetApplicationRow, "vaccine_item_id" | "medical_product_id" | "application_type"> & {
  item_id?: string | null;
  next_application_item_id?: string | null;
  has_next_dose?: boolean | null;
  next_dose_completed_at?: string | null;
  next_dose_completed_by_application_id?: string | null;
  item?: ApplicationItemRelation;
  next_application_item?: ApplicationItemRelation;
  clinics?: { id: string; name: string | null } | null;
  appointment?: { id: string; starts_at: string; type: string; status: string } | null;
  medical_record?: { id: string; consultation_date: string } | null;
};
type CreatePetApplicationInput = Omit<PetApplicationInsert, "vaccine_item_id" | "medical_product_id" | "application_type"> & {
  item_id?: string | null;
  next_application_item_id?: string | null;
  has_next_dose?: boolean;
  next_dose_completed_at?: string | null;
  next_dose_completed_by_application_id?: string | null;
};
type UpdatePetApplicationInput = Omit<PetApplicationUpdate, "vaccine_item_id" | "medical_product_id" | "application_type"> & {
  item_id?: string | null;
  next_application_item_id?: string | null;
  has_next_dose?: boolean;
  next_dose_completed_at?: string | null;
  next_dose_completed_by_application_id?: string | null;
};
type ProductStockUpdate = Database["public"]["Tables"]["product_stock"]["Update"];
type AppointmentFetchRow = Pick<
  Database["public"]["Tables"]["appointments"]["Row"],
  "id" | "pet_id" | "type" | "starts_at" | "status"
>;
type PetApplicationReportRow = Pick<
  Database["public"]["Tables"]["pet_applications"]["Row"],
  "id" | "pet_id" | "application_date" | "created_at" | "item_id"
> & {
  next_application_item_id?: string | null;
  has_next_dose?: boolean | null;
};

type NextApplicationFormEntry = {
  _key: string;
  item_id: string | null;
  item_label: string;
  interval_days: number | null;
  due_date: Date | null;
};

type CreatePetVaccinationInput = Omit<CreatePetApplicationInput, "vet_user_id"> & {
  /**
   * Controls whether we should create/update a calendar appointment when `next_dose_date` is set.
   * Defaults to `true` when omitted for backwards compatibility.
   */
  create_appointment?: boolean;
  next_applications?: NextApplicationFormEntry[];
};

type UpdatePetVaccinationInput = UpdatePetApplicationInput & {
  /**
   * Controls whether we should create/update a calendar appointment when `next_dose_date` is set.
   * Defaults to `true` when omitted for backwards compatibility.
   */
  create_appointment?: boolean;
  next_applications?: NextApplicationFormEntry[];
};

const normalizeDateText = (input?: string | Date | null) => {
  if (!input) return "";

  if (typeof input === "string") {
    if (input.length >= 10) {
      return input.slice(0, 10);
    }
    return input;
  }

  try {
    return formatISO(input, { representation: "date" });
  } catch {
    return "";
  }
};

const supabaseAny = supabase as unknown as {
  from: typeof supabase.from;
  rpc: typeof supabase.rpc;
};
const APPOINTMENTS_PET_BATCH_SIZE = 50;

const invalidateApplicationPlanQueries = (queryClient: ReturnType<typeof useQueryClient>, petId?: string | null, clinicId?: string | null) => {
  if (petId) {
    queryClient.invalidateQueries({ queryKey: ["pet-application-plans", petId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ["pet-application-plans"] });
  }

  if (clinicId) {
    queryClient.invalidateQueries({ queryKey: ["application-plans-due", clinicId] });
    queryClient.invalidateQueries({ queryKey: ["application-plan-report-rows", clinicId] });
    queryClient.invalidateQueries({ queryKey: ["matchable-application-plans", clinicId] });
  } else {
    queryClient.invalidateQueries({ queryKey: ["application-plans-due"] });
    queryClient.invalidateQueries({ queryKey: ["application-plan-report-rows"] });
    queryClient.invalidateQueries({ queryKey: ["matchable-application-plans"] });
  }
};

const fetchAppointmentsForPetIds = async ({
  petIds,
  overallStart,
  overallEnd,
}: {
  petIds: string[];
  overallStart: string;
  overallEnd: string;
}): Promise<AppointmentFetchRow[]> => {
  const uniquePetIds = Array.from(new Set(petIds)).filter(Boolean);
  if (uniquePetIds.length === 0) return [];

  const vaccinationReportAppointmentTypes = ["vacina", "consulta", "retorno", "aplicacao"] as const;
  const appointmentStatuses = ["agendado", "confirmado", "em_atendimento", "cancelado", "faltou"] as const;
  const appointments: AppointmentFetchRow[] = [];

  for (let index = 0; index < uniquePetIds.length; index += APPOINTMENTS_PET_BATCH_SIZE) {
    const batchPetIds = uniquePetIds.slice(index, index + APPOINTMENTS_PET_BATCH_SIZE);
    const { data, error } = await supabase
      .from("appointments")
      .select("id, pet_id, type, starts_at, status")
      .in("pet_id", batchPetIds)
      .gte("starts_at", overallStart)
      .lte("starts_at", overallEnd)
      .in("type", vaccinationReportAppointmentTypes)
      .in("status", appointmentStatuses);

    if (error) throw error;
    appointments.push(...((data ?? []) as AppointmentFetchRow[]));
  }

  return appointments;
};

// Note: item_id is the canonical application catalog reference.

const resolveStockDebitTargets = async ({
  clinicId,
  itemId,
  quantity,
}: {
  clinicId: string;
  itemId: string;
  quantity: number;
}): Promise<Array<{ itemId: string; quantity: number }>> => {
  const normalizedQuantity = Math.max(0, Math.floor(Number(quantity ?? 0)));
  if (normalizedQuantity <= 0) return [];

  const { data: selectedItem, error: selectedItemError } = await supabase
    .from("items")
    .select("id, type")
    .eq("id", itemId)
    .is("deleted_at", null)
    .maybeSingle();

  if (selectedItemError) throw selectedItemError;
  if (!selectedItem?.id) return [];

  if (selectedItem.type !== "PACOTE") {
    return [{ itemId, quantity: normalizedQuantity }];
  }

  const mapRowsToTargets = (rows: Array<{ item_id?: string | null; quantity?: number | null }>) =>
    rows
      .map((component) => ({
        itemId: component.item_id ?? "",
        quantity: Math.max(0, Math.floor(Number(component.quantity ?? 0))) * normalizedQuantity,
      }))
      .filter((entry) => Boolean(entry.itemId))
      .filter((entry) => entry.quantity > 0);

  const { data: rpcRows, error: rpcError } = await supabaseAny.rpc(
    "get_package_stock_components_for_application",
    {
      p_item_id: itemId,
      p_clinic_id: clinicId,
    },
  );

  if (!rpcError) {
    return mapRowsToTargets((rpcRows ?? []) as Array<{ item_id?: string | null; quantity?: number | null }>);
  }

  // Fallback for environments where the RPC is not deployed yet.
  const { data: clinic, error: clinicError } = await supabase
    .from("clinics")
    .select("customer_id")
    .eq("id", clinicId)
    .maybeSingle();

  if (clinicError) throw clinicError;
  if (!clinic?.customer_id) return [];

  const { data: templates, error: templatesError } = await supabaseAny
    .from("catalog_packages")
    .select("id, customer_id")
    .eq("item_id", itemId)
    .eq("customer_id", clinic.customer_id)
    .is("deleted_at", null)
    .limit(1);

  if (templatesError) throw templatesError;
  const templateId = (templates?.[0]?.id as string | undefined) ?? undefined;
  if (!templateId) return [];

  const { data: rawComponents, error: rawComponentsError } = await supabaseAny
    .from("catalog_package_items")
    .select("item_id, sessions_total")
    .eq("catalog_package_id", templateId);

  if (rawComponentsError) throw rawComponentsError;

  const rawRows = (rawComponents ?? []) as Array<{ item_id?: string | null; sessions_total?: number | null }>;
  const componentIds = [...new Set(rawRows.map((row) => row.item_id).filter(Boolean))] as string[];
  if (componentIds.length === 0) return [];

  const { data: componentItems, error: componentItemsError } = await supabase
    .from("items")
    .select("id, type")
    .in("id", componentIds)
    .is("deleted_at", null);

  if (componentItemsError) throw componentItemsError;

  const allowedProductIds = new Set(
    (componentItems ?? []).filter((component) => component.type === "PRODUTO").map((component) => component.id),
  );
  if (allowedProductIds.size === 0) return [];

  const aggregated = new Map<string, number>();
  for (const row of rawRows) {
    if (!row.item_id) continue;
    if (!allowedProductIds.has(row.item_id)) continue;
    const perPackageQuantity = Math.max(0, Math.floor(Number(row.sessions_total ?? 1)));
    if (perPackageQuantity <= 0) continue;

    const current = aggregated.get(row.item_id) ?? 0;
    aggregated.set(row.item_id, current + perPackageQuantity);
  }

  return mapRowsToTargets(
    [...aggregated.entries()].map(([componentItemId, componentQuantity]) => ({
      item_id: componentItemId,
      quantity: componentQuantity,
    })),
  );
};

// Export debitVaccineStock for use when saving vaccinations with medical records
// Uses product_stock table (new simplified structure)
// Stock movements are automatically created by trigger log_product_stock_movement_trigger
export const debitVaccineStock = async ({
  clinicId,
  vaccineItemId,
  batchNumber,
  expiryDate,
  quantity = 1,
  referenceId,
  referenceType = "pet_applications",
  createdBy,
}: {
  clinicId: string;
  vaccineItemId: string | null;
  batchNumber?: string | null;
  expiryDate?: string | Date | null;
  quantity?: number;
  referenceId?: string | null;
  referenceType?: string | null;
  createdBy?: string | null;
}) => {
  if (!clinicId) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.clinicRequiredForStock"));
  }

  if (!vaccineItemId) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.applicationItemNotIdentified"));
  }

  const targets = await resolveStockDebitTargets({
    clinicId,
    itemId: vaccineItemId,
    quantity,
  });

  if (targets.length === 0) {
    console.warn("No stock debit targets resolved for application item", {
      clinicId,
      itemId: vaccineItemId,
      quantity,
    });
    return vaccineItemId;
  }

  for (const target of targets) {
    const { data: existingStock, error: stockSelectError } = await supabase
      .from("product_stock")
      .select("id, quantity")
      .eq("item_id", target.itemId)
      .eq("clinic_id", clinicId)
      .maybeSingle();

    if (stockSelectError) throw stockSelectError;
    if (!existingStock) continue;

    const currentQuantity = Number(existingStock.quantity ?? 0);
    if (currentQuantity <= 0) continue;

    // Never decrement below zero and never create stock rows while consuming.
    const debitQuantity = Math.min(currentQuantity, Math.max(0, Number(target.quantity ?? 0)));
    if (debitQuantity <= 0) continue;

    const { data: item, error: itemError } = await supabase
      .from("items")
      .select("track_by_expiry")
      .eq("id", target.itemId)
      .single();

    if (itemError) throw itemError;

    const newQuantity = currentQuantity - debitQuantity;
    // Update existing stock
    // The trigger will automatically create a movement record
    const updateData: ProductStockUpdate = {
      quantity: newQuantity,
      deleted_at: null,
      deleted_by: null,
    };

    // Only update expiry date if item tracks expiry and expiryDate is provided
    if (item?.track_by_expiry && expiryDate) {
      const normalizedExpiry = normalizeDateText(expiryDate);
      if (normalizedExpiry) {
        updateData.min_expiry_date = normalizedExpiry;
        updateData.has_expiring_items = true;
      }
    }

    const { error: updateError } = await supabase
      .from("product_stock")
      .update(updateData)
      .eq("id", existingStock.id);

    if (updateError) throw updateError;
  }

  // Note: Stock movements are automatically created by the trigger log_product_stock_movement_trigger
  // No need to manually insert into product_stock_movements

  return vaccineItemId;
};

// Helper function to get owner_id from pet_id
const getPetOwnerId = async (petId: string): Promise<string> => {
  const { data: pet, error } = await supabase
    .from("pets")
    .select("owner_id")
    .eq("id", petId)
    .single();

  if (error) throw error;
  if (!pet?.owner_id) throw new Error(i18n.t("patients:phase8.petApplications.errors.petOwnerMissing"));

  return pet.owner_id;
};

const normalizeApplicationSubtype = (value?: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const isAllowedApplicationSubtype = (value?: string | null) => {
  const subtype = normalizeApplicationSubtype(value);
  if (!subtype) return false;
  return (
    subtype.includes("vacina") ||
    subtype.includes("medicament") ||
    subtype.includes("remedio") ||
    subtype.includes("aplicacao") ||
    subtype.includes("application")
  );
};

const resolvePackageApplicationItems = async ({
  clinicId,
  packageItemId,
}: {
  clinicId?: string | null;
  packageItemId: string;
}) => {
  const extractEligibleItemIds = async (
    rows: Array<{ item_id?: string | null }>,
  ): Promise<string[]> => {
    const candidateItemIds = [...new Set(rows.map((row) => row.item_id).filter(Boolean))] as string[];
    if (candidateItemIds.length === 0) return [];

    const { data: candidateItems, error: candidateItemsError } = await supabase
      .from("items")
      .select("id, type, product_subtype:product_subtypes(name)")
      .in("id", candidateItemIds)
      .is("deleted_at", null);
    if (candidateItemsError) throw candidateItemsError;

    return (candidateItems ?? [])
      .filter((item) => item.type === "PRODUTO")
      .filter((item) => isAllowedApplicationSubtype(item.product_subtype?.name))
      .map((item) => item.id);
  };

  if (clinicId) {
    const { data: rpcRows, error: rpcError } = await supabaseAny.rpc(
      "get_package_stock_components_for_application",
      {
        p_item_id: packageItemId,
        p_clinic_id: clinicId,
      },
    );
    if (!rpcError) {
      return extractEligibleItemIds((rpcRows ?? []) as Array<{ item_id?: string | null }>);
    }
  }

  const { data: packageTemplate, error: packageTemplateError } = await supabaseAny
    .from("catalog_packages")
    .select("id")
    .eq("item_id", packageItemId)
    .is("deleted_at", null)
    .limit(1)
    .maybeSingle();
  if (packageTemplateError) throw packageTemplateError;
  if (!packageTemplate?.id) return [];

  const { data: components, error: componentsError } = await supabaseAny
    .from("catalog_package_items")
    .select("item_id")
    .eq("catalog_package_id", packageTemplate.id);
  if (componentsError) throw componentsError;

  return extractEligibleItemIds((components ?? []) as Array<{ item_id?: string | null }>);
};

const resolveApplicationItemIds = async ({
  itemId,
  clinicId,
  allowedDeletedItemIds = [],
}: {
  itemId?: string | null;
  clinicId?: string | null;
  allowedDeletedItemIds?: string[];
}) => {
  if (!itemId) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.applicationNotSelected"));
  }

  const normalizedAllowedDeletedItemIds = new Set(allowedDeletedItemIds.filter(Boolean));

  const { data, error } = await supabase
    .from("items")
    .select("id, type, deleted_at, product_subtype:product_subtypes(name), package_subtype:package_subtypes(name)")
    .eq("id", itemId)
    .maybeSingle();

  if (error) throw error;
  if (!data?.id) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.invalidApplicationItem"));
  }
  if (data.deleted_at && !normalizedAllowedDeletedItemIds.has(data.id)) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.invalidApplicationItem"));
  }

  const subtype = data.type === "PACOTE" ? data.package_subtype?.name : data.product_subtype?.name;
  if (!isAllowedApplicationSubtype(subtype)) {
    throw new Error(i18n.t("patients:phase8.petApplications.errors.invalidApplicationItem"));
  }

  if (data.type === "PACOTE") {
    const packageComponentItemIds = await resolvePackageApplicationItems({
      clinicId,
      packageItemId: data.id,
    });
    if (packageComponentItemIds.length === 0) {
      throw new Error(i18n.t("patients:phase8.petApplications.errors.invalidApplicationItem"));
    }

    return { itemIds: packageComponentItemIds };
  }

  return { itemIds: [data.id as string] };
};

const buildNextApplicationItemMap = ({
  resolvedItemIds,
  resolvedNextApplicationItemIds,
}: {
  resolvedItemIds: string[];
  resolvedNextApplicationItemIds: string[];
}) => {
  if (resolvedNextApplicationItemIds.length === 0) {
    return new Map<string, string | null>();
  }

  const nextIdsSet = new Set(resolvedNextApplicationItemIds);

  return new Map<string, string | null>(
    resolvedItemIds.map((resolvedItemId, index) => {
      if (nextIdsSet.has(resolvedItemId)) {
        return [resolvedItemId, resolvedItemId];
      }

      if (resolvedItemIds.length === resolvedNextApplicationItemIds.length) {
        return [resolvedItemId, resolvedNextApplicationItemIds[index] ?? resolvedNextApplicationItemIds[0] ?? null];
      }

      return [resolvedItemId, resolvedNextApplicationItemIds[0] ?? null];
    }),
  );
};

// Helper function to get vaccine name
const getApplicationName = async ({
  itemId,
  nextApplicationItemId,
}: {
  itemId?: string | null;
  nextApplicationItemId?: string | null;
}): Promise<string> => {
  const targetItemId = nextApplicationItemId ?? itemId;

  if (targetItemId) {
    const { data: item, error } = await supabase
      .from("items")
      .select("name")
      .eq("id", targetItemId)
      .maybeSingle();

    if (!error && item?.name) {
      return item.name;
    }
  }

  return i18n.t("patients:ownerHistory.fallbacks.application");
};

// Helper function to create or update vaccination appointment
export const createOrUpdateVaccinationAppointment = async ({
  petId,
  clinicId,
  nextDoseDate,
  vaccineItemId,
  nextApplicationItemId,
  doseNumber,
  existingAppointmentId,
  vetUserId,
  notes: vaccinationNotes,
  adverseReaction,
}: {
  petId: string;
  clinicId: string;
  nextDoseDate: string | Date;
  vaccineItemId?: string | null;
  nextApplicationItemId?: string | null;
  doseNumber?: number;
  existingAppointmentId?: string | null;
  vetUserId?: string | null;
  notes?: string | null;
  adverseReaction?: string | null;
}): Promise<string | null> => {
  try {
    const { data: pet } = await supabase
      .from("pets")
      .select("death_date, death_cause, status:pet_statuses(name)")
      .eq("id", petId)
      .single();

    if (shouldBlockRemindersForPet(pet)) {
      return null;
    }

    // Get owner_id from pet
    const ownerId = await getPetOwnerId(petId);

    // Get vaccine name for notes
    const vaccineName = await getApplicationName({
      itemId: vaccineItemId,
      nextApplicationItemId,
    });

    // Parse next_dose_date (can be string or Date)
    const startsAt = nextDoseDate instanceof Date ? nextDoseDate : new Date(nextDoseDate);

    // Set ends_at to 30 minutes after starts_at
    const endsAt = new Date(startsAt);
    endsAt.setMinutes(endsAt.getMinutes() + 30);

    // Get current user for created_by_user_id
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error(i18n.t("patients:phase8.petApplications.errors.notAuthenticated"));
    }

    // Build notes combining vaccine info, notes, and adverse reaction
    const notesParts: string[] = [];
    notesParts.push(`Retorno aplicação: ${vaccineName}${doseNumber ? ` - Dose ${doseNumber}` : ""}`);

    if (vaccinationNotes?.trim()) {
      notesParts.push(`\n\nObservações: ${vaccinationNotes.trim()}`);
    }

    if (adverseReaction?.trim()) {
      notesParts.push(`\n\nReação Adversa: ${adverseReaction.trim()}`);
    }

    const notes = notesParts.join("");

    const appointmentData: AppointmentInput = {
      clinic_id: clinicId,
      vet_user_id: vetUserId ?? null,
      owner_id: ownerId,
      pet_ids: [petId],
      type: "aplicacao",
      starts_at: startsAt.toISOString(),
      ends_at: endsAt.toISOString(),
      notes,
      status: "agendado",
    };

    if (existingAppointmentId) {
      // Update existing appointment
      const { data: appointment, error: updateError } = await supabase
        .from("appointments")
        .update({
          starts_at: appointmentData.starts_at,
          ends_at: appointmentData.ends_at,
          notes: appointmentData.notes,
          vet_user_id: appointmentData.vet_user_id,
        })
        .eq("id", existingAppointmentId)
        .select()
        .single();

      if (updateError) throw updateError;
      return appointment.id;
    } else {
      // Create new appointment (+ relation on appointment_pets)
      const { data: appointment, error: insertError } = await supabase
        .from("appointments")
        .insert({
          clinic_id: appointmentData.clinic_id,
          vet_user_id: appointmentData.vet_user_id ?? null,
          owner_id: appointmentData.owner_id,
          pet_id: appointmentData.pet_ids[0],
          type: appointmentData.type,
          starts_at: appointmentData.starts_at,
          ends_at: appointmentData.ends_at,
          notes: appointmentData.notes ?? null,
          status: appointmentData.status ?? "agendado",
          created_by_user_id: user.user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const { error: relationError } = await supabase
        .from("appointment_pets")
        .insert({
          appointment_id: appointment.id,
          pet_id: petId,
        });

      if (relationError) throw relationError;

      return appointment.id;
    }
  } catch (error) {
    console.error("Error creating/updating vaccination appointment:", error);
    // Don't throw - allow vaccination to be saved even if appointment creation fails
    return null;
  }
};

// Helper function to cancel/delete vaccination appointment
const cancelVaccinationAppointment = async (appointmentId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("appointments")
      .update({ status: "cancelado" })
      .eq("id", appointmentId);

    if (error) throw error;
  } catch (error) {
    console.error("Error canceling vaccination appointment:", error);
    // Don't throw - allow vaccination update to proceed
  }
};

const concludeOpenApplicationItems = async ({
  petId,
  clinicId,
  itemIds,
  createdApplicationIds,
  completedApplicationIdByItem,
}: {
  petId: string;
  clinicId: string;
  itemIds: string[];
  createdApplicationIds: string[];
  completedApplicationIdByItem: Map<string, string>;
}) => {
  const uniqueItemIds = Array.from(new Set(itemIds.filter(Boolean)));
  if (uniqueItemIds.length === 0) return;

  const createdIdSet = new Set(createdApplicationIds.filter(Boolean));

  const { data: openApplications, error: openApplicationsError } = await supabaseAny
    .from("pet_applications")
    .select("id, pet_id, item_id, next_application_item_id, appointment_id, has_next_dose")
    .eq("pet_id", petId)
    .eq("has_next_dose", true)
    .is("deleted_at", null);

  if (openApplicationsError) throw openApplicationsError;

  const matchingApplications = ((openApplications ?? []) as Array<{
    id: string;
    pet_id?: string | null;
    item_id?: string | null;
    next_application_item_id?: string | null;
    appointment_id?: string | null;
    has_next_dose?: boolean | null;
  }>).filter((row) => {
    if (!row.id || createdIdSet.has(row.id)) return false;
    const effectiveItemId = row.next_application_item_id ?? row.item_id ?? null;
    return Boolean(effectiveItemId && uniqueItemIds.includes(effectiveItemId));
  });

  const appointmentIdsToCancel = new Set<string>();
  for (const row of matchingApplications) {
    if (row.appointment_id) appointmentIdsToCancel.add(row.appointment_id);
  }

  if (matchingApplications.length > 0) {
    const completedAt = new Date().toISOString();

    for (const row of matchingApplications) {
      const effectiveItemId = row.next_application_item_id ?? row.item_id ?? null;
      const completedByApplicationId =
        effectiveItemId ? completedApplicationIdByItem.get(effectiveItemId) ?? null : null;

      const { error: concludeApplicationError } = await supabaseAny
        .from("pet_applications")
        .update({
          has_next_dose: false,
          next_dose_completed_at: completedAt,
          next_dose_completed_by_application_id: completedByApplicationId,
          appointment_id: null,
        })
        .eq("id", row.id);

      if (concludeApplicationError) throw concludeApplicationError;
    }
  }

  const { data: openPlans, error: openPlansError } = await supabaseAny
    .from("pet_application_plans")
    .select("id, item_id, appointment_id, status")
    .eq("pet_id", petId)
    .eq("clinic_id", clinicId)
    .in("status", ["pending", "scheduled"])
    .is("deleted_at", null);

  if (openPlansError) throw openPlansError;

  const matchingPlans = ((openPlans ?? []) as Array<{
    id: string;
    item_id: string;
    appointment_id?: string | null;
    status: "pending" | "scheduled" | "completed" | "canceled";
  }>).filter((plan) => uniqueItemIds.includes(plan.item_id));

  for (const plan of matchingPlans) {
    if (plan.appointment_id) appointmentIdsToCancel.add(plan.appointment_id);
  }

  for (const appointmentId of appointmentIdsToCancel) {
    await cancelVaccinationAppointment(appointmentId);
  }

  for (const plan of matchingPlans) {
    const completedPetApplicationId = completedApplicationIdByItem.get(plan.item_id);
    if (!completedPetApplicationId) continue;

    const { error: completePlanError } = await supabaseAny.rpc("complete_pet_application_plan", {
      p_plan_id: plan.id,
      p_completed_pet_application_id: completedPetApplicationId,
      p_completed_at: new Date().toISOString(),
    });

    if (completePlanError) throw completePlanError;
  }
};

export type NextApplicationItemInput = {
  item_id: string;
  due_date: string;
};

export const insertPetApplicationNextItems = async (
  petApplicationId: string,
  items: NextApplicationItemInput[],
): Promise<void> => {
  if (!items.length) return;

  const rows = items.map((item) => ({
    pet_application_id: petApplicationId,
    item_id: item.item_id,
    due_date: item.due_date,
  }));

  const { error } = await supabaseAny
    .from("pet_application_next_items")
    .insert(rows);

  if (error) throw error;
};

export const createPlansForNextItems = async (
  petId: string,
  clinicId: string,
  petApplicationId: string,
  items: NextApplicationItemInput[],
  userId: string,
): Promise<void> => {
  if (!items.length) return;

  const planRows = items.map((item) => ({
    pet_id: petId,
    clinic_id: clinicId,
    item_id: item.item_id,
    due_date: item.due_date,
    source_type: "derived_from_application",
    source_pet_application_id: petApplicationId,
    status: "pending",
    created_by: userId,
  }));

  const { error } = await supabaseAny
    .from("pet_application_plans")
    .insert(planRows);

  if (error) {
    console.warn("Warning: Could not create application plans for next items:", error);
  }
};

export const usePetVaccinations = (petId?: string) => {
  return useQuery({
    queryKey: ["pet-applications", petId],
    enabled: Boolean(petId),
    queryFn: async () => {
      const { data, error } = await supabaseAny
        .from("pet_applications")
        .select(`
          *,
          clinics(id, name),
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          next_application_item:items!next_application_item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          appointment:appointments(id, starts_at, type, status),
          medical_record:medical_records(id, consultation_date)
        `)
        .eq("pet_id", petId!)
        .is("deleted_at", null)
        .order("application_date", { ascending: false });

      if (error) throw error;
      return (data ?? []) as PetApplication[];
    },
  });
};

export const usePetVaccination = (id?: string) => {
  return useQuery({
    queryKey: ["pet-application", id],
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await supabaseAny
        .from("pet_applications")
        .select(`
          *,
          clinics(id, name),
          pets(id, name),
          item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          next_application_item:items!next_application_item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
          appointment:appointments(id, starts_at, type, status),
          medical_record:medical_records(id, consultation_date)
        `)
        .eq("id", id!)
        .is("deleted_at", null)
        .single();

      if (error) throw error;
      return data as PetApplication | null;
    },
  });
};

export const useCreatePetVaccination = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: CreatePetVaccinationInput) => {
      if (!user?.id) {
        throw new Error(i18n.t("patients:phase8.petApplications.errors.notAuthenticated"));
      }
      const { create_appointment, next_applications, ...vaccination } = input;
      const shouldCreateAppointment = create_appointment ?? false;
      const hasNextDose = Boolean(vaccination.has_next_dose);

      const normalizedItemId =
        typeof vaccination.item_id === "string" && vaccination.item_id.trim()
          ? vaccination.item_id.trim()
          : null;
      const normalizedNextApplicationItemId =
        hasNextDose && typeof vaccination.next_application_item_id === "string" && vaccination.next_application_item_id.trim()
          ? vaccination.next_application_item_id.trim()
          : null;

      const { itemIds } = await resolveApplicationItemIds({
        itemId: normalizedItemId,
        clinicId: vaccination.clinic_id ?? null,
      });
      const primaryItemId = itemIds[0] ?? null;
      const resolvedNextApplication =
        hasNextDose && normalizedNextApplicationItemId
          ? await resolveApplicationItemIds({
            itemId: normalizedNextApplicationItemId,
            clinicId: vaccination.clinic_id ?? null,
          })
          : null;
      const resolvedNextApplicationItemIds = resolvedNextApplication?.itemIds ?? [];
      const nextApplicationItemMap = buildNextApplicationItemMap({
        resolvedItemIds: itemIds,
        resolvedNextApplicationItemIds,
      });
      const primaryNextApplicationItemId =
        nextApplicationItemMap.get(primaryItemId ?? "") ?? resolvedNextApplicationItemIds[0] ?? null;

      const insertRows = itemIds.map((resolvedItemId) => ({
        ...vaccination,
        vet_user_id: user.id,
        item_id: resolvedItemId,
        has_next_dose: hasNextDose,
        next_application_item_id: hasNextDose
          ? nextApplicationItemMap.get(resolvedItemId) ?? primaryNextApplicationItemId
          : null,
        next_dose_date: hasNextDose ? vaccination.next_dose_date ?? null : null,
        next_dose_completed_at: null,
        next_dose_completed_by_application_id: null,
        appointment_id: hasNextDose ? vaccination.appointment_id ?? null : null,
      }));

      const { data, error } = await supabaseAny
        .from("pet_applications")
        .insert(insertRows)
        .select()
      const createdRows = (data ?? []) as PetApplicationRow[];

      if (error) throw error;
      if (createdRows.length === 0) {
        throw new Error(i18n.t("patients:phase8.petApplications.createErrorTitle"));
      }
      const primaryRow = createdRows[0];

      // Attempt to debit stock - allow negative stock values
      // Don't rollback vaccination if stock update fails
      try {
        for (const row of createdRows) {
          await debitVaccineStock({
            clinicId: row.clinic_id,
            vaccineItemId: row.item_id,
            batchNumber: row.batch_number,
            expiryDate: row.expiry_date,
            quantity: 1,
            referenceId: row.id,
            createdBy: user.id,
          });
        }
      } catch (stockError) {
        // Log error but don't prevent vaccination from being saved
        // Stock can be negative, which is acceptable
        console.warn("Warning: Could not update vaccine stock (stock may go negative):", stockError);
      }

      try {
        const resolvedItemIds = createdRows.map((row) => row.item_id).filter(Boolean) as string[];
        const completedApplicationIdByItem = new Map(
          createdRows
            .filter((row) => Boolean(row.item_id && row.id))
            .map((row) => [row.item_id, row.id] as const),
        );
        if (primaryRow?.pet_id && primaryRow?.clinic_id && resolvedItemIds.length > 0) {
          await concludeOpenApplicationItems({
            petId: primaryRow.pet_id,
            clinicId: primaryRow.clinic_id,
            itemIds: resolvedItemIds,
            createdApplicationIds: createdRows.map((row) => row.id),
            completedApplicationIdByItem,
          });
        }
      } catch (concludeError) {
        console.warn("Warning: Could not conclude open application items:", concludeError);
      }

      // Create appointment if next_dose_date is set
      if (shouldCreateAppointment && primaryRow.next_dose_date) {
        try {
          const appointmentId = await createOrUpdateVaccinationAppointment({
            petId: primaryRow.pet_id,
            clinicId: primaryRow.clinic_id,
            nextDoseDate: primaryRow.next_dose_date,
            vaccineItemId: primaryItemId,
            nextApplicationItemId: primaryRow.next_application_item_id ?? primaryNextApplicationItemId,
            doseNumber: primaryRow.dose_number,
            existingAppointmentId: primaryRow.appointment_id || undefined,
            vetUserId: user.id,
            notes: primaryRow.notes || undefined,
            adverseReaction: primaryRow.adverse_reaction || undefined,
          });

          // Update all created application rows with the generated appointment_id
          if (appointmentId && createdRows.some((row) => row.appointment_id !== appointmentId)) {
            const createdIds = createdRows.map((row) => row.id);
            const { error: updateError } = await supabaseAny
              .from("pet_applications")
              .update({ appointment_id: appointmentId })
              .in("id", createdIds);

            if (updateError) {
              console.warn("Could not update vaccination with appointment_id:", updateError);
            } else {
              createdRows.forEach((row) => {
                row.appointment_id = appointmentId;
              });
            }
          }
        } catch (appointmentError) {
          // Log error but don't prevent vaccination from being saved
          console.warn("Warning: Could not create vaccination appointment:", appointmentError);
        }
      }

      try {
        for (const row of createdRows) {
          await supabaseAny.rpc("sync_pet_application_derived_plan", {
            p_pet_application_id: row.id,
          });
        }
      } catch (derivedPlanError) {
        console.warn("Warning: Could not sync derived application plan:", derivedPlanError);
      }

      // Save multi next-application items if provided
      const nextApplicationItems: NextApplicationItemInput[] =
        (next_applications ?? [])
          .filter((e) => e.item_id && e.due_date)
          .map((e) => ({
            item_id: e.item_id as string,
            due_date: toLocalNoonISOString(e.due_date as Date),
          }));

      if (nextApplicationItems.length > 0 && primaryRow) {
        try {
          await insertPetApplicationNextItems(primaryRow.id, nextApplicationItems);
          await createPlansForNextItems(
            primaryRow.pet_id,
            primaryRow.clinic_id,
            primaryRow.id,
            nextApplicationItems,
            user.id,
          );
        } catch (nextItemsError) {
          console.warn("Warning: Could not save next application items:", nextItemsError);
        }
      }

      const result = createdRows[0] as PetApplicationRow & { created_ids?: string[] };
      result.created_ids = createdRows.map((row) => row.id);
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pet-applications", variables.pet_id] });
      queryClient.invalidateQueries({ queryKey: ["application-stats"] });
      queryClient.invalidateQueries({ queryKey: ["applications-with-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["future-rows-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      invalidateApplicationPlanQueries(queryClient, variables.pet_id, variables.clinic_id);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("patients:phase8.petApplications.createdTitle"));
    },
    onError: (error) => {
      console.error("Error creating pet vaccination:", error);
      toast.error(i18n.t("patients:phase8.petApplications.createErrorTitle"));
    },
  });
};

export const useUpdatePetVaccination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      cancelAppointment,
      create_appointment,
      next_applications: _nextApplications,
      ...vaccination
    }: UpdatePetVaccinationInput & {
      id: string;
      cancelAppointment?: boolean;
    }) => {
      // Get existing vaccination to compare next_dose_date
      const { data: existingVaccination, error: fetchError } = await supabaseAny
        .from("pet_applications")
        .select("next_dose_date, appointment_id, pet_id, clinic_id, item_id, next_application_item_id, has_next_dose, dose_number, notes, adverse_reaction, vet_user_id, next_dose_completed_at, next_dose_completed_by_application_id")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      const oldNextDoseDate = existingVaccination?.next_dose_date;
      // Check if next_dose_date is explicitly set in the update (could be null, undefined, or a date)
      const nextDoseDateKey = "next_dose_date" as keyof typeof vaccination;
      const hasNextDoseDateInUpdate = nextDoseDateKey in vaccination;
      const newNextDoseDate = hasNextDoseDateInUpdate
        ? (vaccination.next_dose_date ?? null)
        : oldNextDoseDate; // Not updated, keep old value
      const existingAppointmentId = existingVaccination?.appointment_id;
      const shouldCreateAppointment = create_appointment ?? false;
      const hasNextDose =
        "has_next_dose" in vaccination
          ? Boolean(vaccination.has_next_dose)
          : Boolean(existingVaccination?.has_next_dose ?? oldNextDoseDate);

      const normalizedNextApplicationItemId =
        hasNextDose && typeof vaccination.next_application_item_id === "string" && vaccination.next_application_item_id.trim()
          ? vaccination.next_application_item_id.trim()
          : null;
      const resolvedNextApplication =
        hasNextDose && normalizedNextApplicationItemId
          ? await resolveApplicationItemIds({
            itemId: normalizedNextApplicationItemId,
            clinicId: existingVaccination?.clinic_id ?? null,
            allowedDeletedItemIds: [
              existingVaccination?.item_id ?? "",
              existingVaccination?.next_application_item_id ?? "",
            ],
          })
          : null;
      const normalizedItemId =
        typeof vaccination.item_id === "string" && vaccination.item_id.trim()
          ? vaccination.item_id.trim()
          : null;
      const resolvedItem =
        normalizedItemId
          ? await resolveApplicationItemIds({
            itemId: normalizedItemId,
            clinicId: existingVaccination?.clinic_id ?? null,
            allowedDeletedItemIds: [existingVaccination?.item_id ?? ""],
          })
          : null;

      const updatePayload: UpdatePetApplicationInput = {
        ...vaccination,
        item_id: resolvedItem?.itemIds?.[0] ?? vaccination.item_id ?? existingVaccination.item_id ?? null,
        has_next_dose: hasNextDose,
        next_application_item_id: hasNextDose
          ? resolvedNextApplication?.itemIds?.[0] ?? existingVaccination.next_application_item_id ?? null
          : (
            "next_application_item_id" in vaccination
              ? (vaccination.next_application_item_id ?? null)
              : existingVaccination.next_application_item_id ?? null
          ),
        next_dose_date: hasNextDose
          ? newNextDoseDate
          : (
            hasNextDoseDateInUpdate
              ? (vaccination.next_dose_date ?? null)
              : existingVaccination.next_dose_date ?? null
          ),
        next_dose_completed_at: hasNextDose
          ? null
          : (
            "next_dose_completed_at" in vaccination
              ? (vaccination.next_dose_completed_at ?? null)
              : existingVaccination.next_dose_completed_at ?? null
          ),
        next_dose_completed_by_application_id: hasNextDose
          ? null
          : (
            "next_dose_completed_by_application_id" in vaccination
              ? (vaccination.next_dose_completed_by_application_id ?? null)
              : existingVaccination.next_dose_completed_by_application_id ?? null
          ),
        appointment_id: hasNextDose ? vaccination.appointment_id ?? existingAppointmentId ?? null : null,
      };

      // Update the vaccination
      const { data, error } = await supabaseAny
        .from("pet_applications")
        .update(updatePayload)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // Handle appointment creation/update/cancellation
      try {
        // If we explicitly disabled appointment creation, never create/update appointments.
        if (!shouldCreateAppointment) {
          if (cancelAppointment === true && existingAppointmentId) {
            await cancelVaccinationAppointment(existingAppointmentId);

            const { error: updateError } = await supabaseAny
              .from("pet_applications")
              .update({ appointment_id: null })
              .eq("id", id);

            if (updateError) {
              console.warn("Could not clear appointment_id from vaccination:", updateError);
            } else {
              data.appointment_id = null;
            }
          }
        } else {
          // Case 1: next_dose_date was added or changed (has a value)
          if (newNextDoseDate) {
            const appointmentId = await createOrUpdateVaccinationAppointment({
              petId: existingVaccination.pet_id,
              clinicId: existingVaccination.clinic_id,
              nextDoseDate: newNextDoseDate,
            vaccineItemId: resolvedItem?.itemIds?.[0] ?? vaccination.item_id ?? existingVaccination.item_id,
              nextApplicationItemId:
              resolvedNextApplication?.itemIds?.[0] ??
                vaccination.next_application_item_id ??
                existingVaccination.next_application_item_id,
              doseNumber: vaccination.dose_number ?? existingVaccination.dose_number,
              existingAppointmentId: existingAppointmentId || undefined,
              vetUserId: vaccination.vet_user_id ?? existingVaccination.vet_user_id ?? null,
              notes: vaccination.notes ?? existingVaccination.notes ?? undefined,
              adverseReaction: vaccination.adverse_reaction ?? existingVaccination.adverse_reaction ?? undefined,
            });

            // Update vaccination with appointment_id if it changed
            if (appointmentId && appointmentId !== existingAppointmentId) {
              const { error: updateError } = await supabaseAny
                .from("pet_applications")
                .update({ appointment_id: appointmentId })
                .eq("id", id);

              if (updateError) {
                console.warn("Could not update vaccination with appointment_id:", updateError);
              } else {
                data.appointment_id = appointmentId;
              }
            }
          }
          // Case 2: next_dose_date was explicitly removed (was set but now is null/undefined)
          else if (hasNextDoseDateInUpdate && oldNextDoseDate && !newNextDoseDate) {
            if (cancelAppointment === true && existingAppointmentId) {
              // Cancel the appointment
              await cancelVaccinationAppointment(existingAppointmentId);

              // Clear appointment_id from vaccination
              const { error: updateError } = await supabaseAny
                .from("pet_applications")
                .update({ appointment_id: null })
                .eq("id", id);

              if (updateError) {
                console.warn("Could not clear appointment_id from vaccination:", updateError);
              } else {
                data.appointment_id = null;
              }
            }
            // If cancelAppointment is false or undefined, keep the appointment_id
          }
        }
      } catch (appointmentError) {
        // Log error but don't prevent vaccination update
        console.warn("Warning: Could not manage vaccination appointment:", appointmentError);
      }

      try {
        await supabaseAny.rpc("sync_pet_application_derived_plan", {
          p_pet_application_id: id,
        });
      } catch (derivedPlanError) {
        console.warn("Warning: Could not sync derived application plan:", derivedPlanError);
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["pet-applications", data.pet_id] });
      queryClient.invalidateQueries({ queryKey: ["pet-application", data.id] });
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["future-rows-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["applications-with-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      invalidateApplicationPlanQueries(queryClient, data.pet_id, data.clinic_id);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("patients:phase8.petApplications.updatedTitle"));
    },
    onError: (error) => {
      console.error("Error updating pet vaccination:", error);
      toast.error(i18n.t("patients:phase8.petApplications.updateErrorTitle"));
    },
  });
};

export const useDeletePetVaccination = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc("soft_delete_pet_application", {
        p_application_id: id,
        p_deleted_at: new Date().toISOString(),
      });

      if (error) throw error;
      return data as string | null;
    },
    onSuccess: (petId) => {
      if (petId) {
        queryClient.invalidateQueries({ queryKey: ["pet-applications", petId] });
      }
      queryClient.invalidateQueries({ queryKey: ["application-stats"] });
      queryClient.invalidateQueries({ queryKey: ["applications-with-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["future-rows-appointments"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history"] });
      queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
      invalidateApplicationPlanQueries(queryClient, petId ?? undefined, undefined);
      invalidateReportsCache(queryClient);
      toast.success(i18n.t("patients:phase8.petApplications.deletedTitle"));
    },
    onError: (error) => {
      console.error("Error deleting pet vaccination:", error);
      const message =
        error instanceof Error && error.message.toLowerCase().includes("failed to fetch")
          ? i18n.t("patients:phase8.petApplications.errors.supabaseConnectionFailed")
          : i18n.t("patients:phase8.petApplications.deleteErrorTitle");

      toast.error(message);
    },
  });
};

// Get vaccinations due (overdue or upcoming)
export const useVaccinationsDue = (clinicId?: string, days: number = 30) => {
  return useQuery({
    queryKey: ["applications-due", clinicId, days],
    queryFn: async () => {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + days);

      let query = supabaseAny
        .from("pet_applications")
        .select(`
          *,
          pets(id, name, record_number, owners(id, name)),
          item:items!item_id(id, name, product_subtype:product_subtypes(name)),
          next_application_item:items!next_application_item_id(id, name, product_subtype:product_subtypes(name))
        `)
        .not("next_dose_date", "is", null)
        .eq("has_next_dose", true)
        .lte("next_dose_date", toISODateOnlyLocal(futureDate))
        .is("deleted_at", null);

      if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }

      const { data, error } = await query.order("next_dose_date");

      if (error) throw error;
      return data;
    },
  });
};

// Get vaccination statistics
// startDate/endDate: YYYY-MM-DD strings to avoid timezone shift (Date.toISOString uses UTC)
export const useVaccinationStats = (
  clinicId?: string,
  startDate?: Date | string,
  endDate?: Date | string
) => {
  const startStr =
    typeof startDate === "string"
      ? `${startDate.slice(0, 10)}T00:00:00`
      : startDate
        ? `${toISODateOnlyLocal(startDate)}T00:00:00`
        : undefined;
  const endStr =
    typeof endDate === "string"
      ? `${endDate.slice(0, 10)}T23:59:59.999`
      : endDate
        ? `${toISODateOnlyLocal(endDate)}T23:59:59.999`
        : undefined;

  return useQuery({
    queryKey: ["application-stats", clinicId, startStr, endStr],
    enabled: !!clinicId,
    queryFn: async () => {
      let query = supabaseAny
        .from("pet_applications")
        .select(`
          id,
          application_date,
          item_id,
          next_application_item_id,
          pet_id,
          medical_record_id,
          item:items!item_id(id, name, product_subtype:product_subtypes(name)),
          pets(id, name, record_number, owners(id, name)),
          medical_record:medical_records(id, consultation_date)
        `)
        .is("deleted_at", null);

      if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }

      if (startStr) {
        query = query.gte("application_date", startStr);
      }

      if (endStr) {
        query = query.lte("application_date", endStr);
      }

      const { data, error } = await query.order("application_date", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
};

// Get vaccinations with appointment status
export const useVaccinationsWithAppointments = (clinicId?: string, days: number = 30) => {
  return useQuery({
    queryKey: ["applications-with-appointments", clinicId, days],
    enabled: !!clinicId,
    queryFn: async () => {
      const today = new Date();
      const futureDate = new Date();
      futureDate.setDate(today.getDate() + days);

      // Fetch vaccinations due
      let vaccinationsQuery = supabaseAny
        .from("pet_applications")
        .select(`
          *,
          pets(
            id,
            name,
            record_number,
            death_date,
            status:pet_statuses(name),
            owners(
              id,
              name,
              owners_phones(phone_number, is_primary, is_whatsapp, deleted_at),
              owner_whatsapp_identities(wa_id, user_id, parent_user_id, username)
            )
          ),
          item:items!item_id(id, name, product_subtype:product_subtypes(name))
          ,
          next_application_item:items!next_application_item_id(id, name, product_subtype:product_subtypes(name))
        `)
        .not("next_dose_date", "is", null)
        .eq("has_next_dose", true)
        .lte("next_dose_date", toISODateOnlyLocal(futureDate))
        .is("deleted_at", null);

      if (clinicId) {
        vaccinationsQuery = vaccinationsQuery.eq("clinic_id", clinicId);
      }

      const { data: rawVaccinations, error: vaccinationsError } = await vaccinationsQuery.order("next_dose_date");

      if (vaccinationsError) throw vaccinationsError;

      if (!rawVaccinations || rawVaccinations.length === 0) {
        return [];
      }

      const vaccinations = rawVaccinations;

      const petIdsAll = [...new Set((vaccinations ?? []).map((v) => v.pet_id).filter(Boolean))] as string[];
      const candidateNextDoseDates = (vaccinations ?? [])
        .map((v) => (v.next_dose_date ? new Date(v.next_dose_date) : null))
        .filter((d): d is Date => !!d);

      if (petIdsAll.length === 0 || candidateNextDoseDates.length === 0) {
        return (vaccinations ?? []).map((vacc) => ({ ...vacc, appointments: [] }));
      }

      const itemIdsWanted = [
        ...new Set(
          (vaccinations ?? [])
            .map((v) => getVaccinationNextItemId(v))
            .filter((id): id is string => typeof id === "string" && !!id),
        ),
      ];

      const minApplicationDate = (vaccinations ?? [])
        .map((v) => (typeof v.application_date === "string" ? v.application_date : null))
        .filter((d): d is string => !!d)
        .sort()[0];

      let allRows: VaccinationKeyInput[] = vaccinations;
      if (itemIdsWanted.length > 0 && minApplicationDate) {
        const { data: fetchedAllRows, error: allRowsErr } = await supabaseAny.rpc(
          "get_pet_applications_report_rows",
          {
            p_pet_ids: petIdsAll,
            p_item_ids: itemIdsWanted,
            p_min_application_date: minApplicationDate,
            p_clinic_id: clinicId ?? null,
          },
        );
        if (allRowsErr) throw allRowsErr;
        allRows = (fetchedAllRows ?? []) as PetApplicationReportRow[] ;
      }

      const latestCandidates = filterToLatestVaccinations(vaccinations, allRows);

      const petIds = [...new Set((latestCandidates ?? []).map((v) => v.pet_id).filter(Boolean))] as string[];
      const nextDoseDates = (latestCandidates ?? [])
        .map((v) => (v.next_dose_date ? new Date(v.next_dose_date) : null))
        .filter((d): d is Date => !!d);

      if (petIds.length === 0 || nextDoseDates.length === 0) {
        return (latestCandidates ?? []).map((vacc) => ({ ...vacc, appointments: [] }));
      }

      // Single appointments fetch for the overall window: [min(next_dose)-7, max(next_dose)+7]
      const minNextDose = new Date(Math.min(...nextDoseDates.map((d) => d.getTime())));
      const maxNextDose = new Date(Math.max(...nextDoseDates.map((d) => d.getTime())));
      const overallStart = subDays(minNextDose, 7).toISOString();
      const overallEnd = addDays(maxNextDose, 7).toISOString();

      const appointments = await fetchAppointmentsForPetIds({
        petIds,
        overallStart,
        overallEnd,
      });

      const byPetId: Record<string, { id: string; type: string | null; starts_at: string; status: string | null }[]> =
        {};
      const appointmentRows = appointments as AppointmentFetchRow[];
      for (const appt of appointmentRows) {
        const pid = appt.pet_id;
        if (!byPetId[pid]) byPetId[pid] = [];
        byPetId[pid].push({
          id: appt.id,
          type: appt.type ?? null,
          starts_at: appt.starts_at,
          status: appt.status ?? null,
        });
      }

      const vaccinationsWithAppointments = (latestCandidates ?? []).map((vacc) => {
        const nextDoseDate = vacc.next_dose_date ? new Date(vacc.next_dose_date) : null;
        if (!nextDoseDate) {
          return { ...vacc, appointments: [] };
        }

        const searchStartDate = subDays(nextDoseDate, 7).getTime();
        const searchEndDate = addDays(nextDoseDate, 7).getTime();

        const list = (byPetId[vacc.pet_id] ?? []).filter((app) => {
          const t = new Date(app.starts_at).getTime();
          return t >= searchStartDate && t <= searchEndDate;
        });

        list.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

        return {
          ...vacc,
          appointments: list,
        };
      });

      return vaccinationsWithAppointments;
    },
  });
};

export const usePetApplications = usePetVaccinations;
export const usePetApplication = usePetVaccination;
export const useCreatePetApplication = useCreatePetVaccination;
export const useUpdatePetApplication = useUpdatePetVaccination;
export const useDeletePetApplication = useDeletePetVaccination;
export const useApplicationsDue = useVaccinationsDue;
export const useApplicationStats = useVaccinationStats;
export const useApplicationsWithAppointments = useVaccinationsWithAppointments;
